import { useCallback, useEffect, useState } from "react";

import Logo from "../assets/images/IndusindLogo.png";
import CarePlixLogo from "../assets/images/CarePlixLogo.png";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";

const TOKEN_KEY = "admin_token";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch (_) {
    return value;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  } catch (_) {
    return value;
  }
};

const AdminPanel = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const [activeTab, setActiveTab] = useState("export");

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [downloading, setDownloading] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportInfo, setExportInfo] = useState("");

  const [purgeStart, setPurgeStart] = useState(today);
  const [purgeEnd, setPurgeEnd] = useState(today);
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState("");
  const [purgeInfo, setPurgeInfo] = useState("");
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [certificates, setCertificates] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [certsError, setCertsError] = useState("");

  const persistToken = (t) => {
    setToken(t);
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoginError("");
    setLoggingIn(true);
    try {
      const resp = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await resp.json();
      if (resp.ok && data.success && data.token) {
        persistToken(data.token);
        setUsername("");
        setPassword("");
      } else {
        setLoginError(data.message || "Login failed.");
      }
    } catch (err) {
      setLoginError("Network error. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    persistToken("");
    setExportError("");
    setExportInfo("");
    setPurgeError("");
    setPurgeInfo("");
    setCertificates([]);
  };

  const handleSessionExpired = useCallback((setter) => {
    persistToken("");
    setter("Your session has expired. Please log in again.");
  }, []);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setExportError("");
    setExportInfo("");
    if (!startDate || !endDate) {
      setExportError("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      setExportError("Start date must be on or before end date.");
      return;
    }
    setDownloading(true);
    try {
      const resp = await fetch(`/api/admin/export?start=${startDate}&end=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 401) {
        handleSessionExpired(setExportError);
        return;
      }
      if (!resp.ok) {
        let msg = "Failed to generate export.";
        try {
          const data = await resp.json();
          msg = data.message || msg;
        } catch (_) {}
        setExportError(msg);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `careplix-export_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportInfo("Download started. Check your downloads folder.");
    } catch (err) {
      setExportError("Network error. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const fetchCertificates = useCallback(async () => {
    if (!token) return;
    setLoadingCerts(true);
    setCertsError("");
    try {
      const resp = await fetch("/api/admin/purge-certificates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 401) {
        handleSessionExpired(setCertsError);
        return;
      }
      const data = await resp.json();
      if (resp.ok && data.success) {
        setCertificates(data.certificates || []);
      } else {
        setCertsError(data.message || "Failed to load certificates.");
      }
    } catch (err) {
      setCertsError("Network error. Please try again.");
    } finally {
      setLoadingCerts(false);
    }
  }, [token, handleSessionExpired]);

  const openConfirm = (e) => {
    e.preventDefault();
    setPurgeError("");
    setPurgeInfo("");
    if (!purgeStart || !purgeEnd) {
      setPurgeError("Please select both start and end dates.");
      return;
    }
    if (purgeStart > purgeEnd) {
      setPurgeError("Start date must be on or before end date.");
      return;
    }
    setConfirmText("");
    setShowPurgeConfirm(true);
  };

  const handlePurge = async () => {
    if (purging) return;
    setPurging(true);
    setPurgeError("");
    setPurgeInfo("");
    try {
      const resp = await fetch("/api/admin/purge", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ start: purgeStart, end: purgeEnd, confirm: "DELETE" }),
      });
      if (resp.status === 401) {
        setShowPurgeConfirm(false);
        handleSessionExpired(setPurgeError);
        return;
      }
      const data = await resp.json();
      if (resp.ok && data.success) {
        const c = data.certificate;
        setPurgeInfo(
          `Purge complete. ${c.total_deleted} record(s) deleted. Certificate ${c.certificate_number} issued.`
        );
        setShowPurgeConfirm(false);
        fetchCertificates();
      } else {
        setPurgeError(data.message || "Failed to purge data.");
      }
    } catch (err) {
      setPurgeError("Network error. Please try again.");
    } finally {
      setPurging(false);
    }
  };

  const downloadCertificate = (c) => {
    const issued = formatDateTime(c.created_at);
    const html = `<!doctype html><html><head><meta charset="utf-8" />
<title>${c.certificate_number}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;margin:0;padding:40px;}
  .frame{max-width:720px;margin:0 auto;border:2px solid #1845a3;border-radius:16px;padding:40px;}
  .head{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #ED1C24;padding-bottom:16px;}
  .brand{font-size:18px;font-weight:bold;color:#1845a3;}
  .sub{font-size:11px;color:#6b7280;}
  h1{font-size:22px;color:#1845a3;text-align:center;margin:28px 0 4px;}
  .cert-no{text-align:center;font-size:13px;color:#6b7280;margin-bottom:24px;}
  table{width:100%;border-collapse:collapse;margin-top:8px;}
  td{padding:9px 6px;font-size:13px;border-bottom:1px solid #e5e7eb;}
  td.label{color:#6b7280;width:45%;}
  td.value{font-weight:600;}
  .hash{word-break:break-all;font-family:monospace;font-size:11px;color:#374151;}
  .note{margin-top:24px;font-size:11px;color:#6b7280;line-height:1.5;}
  .foot{margin-top:28px;text-align:center;font-size:11px;color:#9ca3af;}
  @media print{.noprint{display:none;}}
</style></head>
<body>
  <div class="frame">
    <div class="head">
      <div class="brand">IndusInd General Insurance</div>
      <div class="sub">Powered by CarePlix</div>
    </div>
    <h1>Data Purging Certificate</h1>
    <div class="cert-no">${c.certificate_number}</div>
    <table>
      <tr><td class="label">Data period purged</td><td class="value">${formatDate(c.start_date)} &ndash; ${formatDate(c.end_date)}</td></tr>
      <tr><td class="label">Family scans deleted</td><td class="value">${c.family_scans_deleted}</td></tr>
      <tr><td class="label">Health questionnaires deleted</td><td class="value">${c.health_questionnaire_deleted}</td></tr>
      <tr><td class="label">Insurance questionnaires deleted</td><td class="value">${c.insurance_questionnaire_deleted}</td></tr>
      <tr><td class="label">Total records deleted</td><td class="value">${c.total_deleted}</td></tr>
      <tr><td class="label">Performed by</td><td class="value">${c.performed_by}</td></tr>
      <tr><td class="label">Issued at</td><td class="value">${issued}</td></tr>
      <tr><td class="label">Integrity hash (SHA-256)</td><td class="value hash">${c.content_hash}</td></tr>
    </table>
    <p class="note">This certificate confirms that the records listed above were permanently deleted from the CarePlix IndusInd database for the stated period. The SHA-256 integrity hash is computed from the certificate contents and can be used to verify that this record has not been altered.</p>
    <div class="foot">Generated by CarePlix on behalf of IndusInd General Insurance.</div>
    <div class="noprint" style="text-align:center;margin-top:24px;">
      <button onclick="window.print()" style="padding:10px 24px;border:none;border-radius:8px;background:#1845a3;color:#fff;font-size:14px;cursor:pointer;">Print / Save as PDF</button>
    </div>
  </div>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  useEffect(() => {
    document.title = "Admin | CarePlix IndusInd";
  }, []);

  useEffect(() => {
    if (token && activeTab === "certificates") {
      fetchCertificates();
    }
  }, [token, activeTab, fetchCertificates]);

  const tabClass = (tab) =>
    `flex-1 py-2.5 text-xs font-semibold rounded-xl transition ${
      activeTab === tab
        ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white shadow"
        : "text-gray-600 hover:bg-gray-100"
    }`;

  const inputClass =
    "mt-1.5 w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#1845a3] outline-none text-sm text-gray-800";

  return (
    <main className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center">
          <img className="h-10 brightness-0 invert" src={Logo} alt="IndusInd" />
          <div className="w-px h-7 bg-white/50 mx-3" />
          <div className="flex flex-col items-start">
            <span className="text-white/80 text-[10px] leading-tight font-medium">Powered by</span>
            <img className="h-4 mt-0.5 object-contain brightness-0 invert" src={CarePlixLogo} alt="CarePlix" />
          </div>
        </div>
        <span className="text-white text-sm font-semibold tracking-wide">Admin</span>
      </header>

      <section className="flex-1 flex items-start justify-center px-4 py-10">
        {!token ? (
          <form onSubmit={handleLogin} className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-6">
            <h1 className="text-lg font-bold text-[#1845a3]">Admin Login</h1>
            <p className="mt-1 text-xs text-gray-500">Authorized personnel only.</p>

            <label className="block mt-5 text-xs font-medium text-gray-600" htmlFor="admin-username">Username</label>
            <input
              id="admin-username"
              className={inputClass}
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loggingIn}
            />

            <label className="block mt-4 text-xs font-medium text-gray-600" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              className={inputClass}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loggingIn}
            />

            {loginError.length > 0 && <p className="mt-3 text-xs text-error">{loginError}</p>}

            <button
              className="mt-6 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#1845a3] to-[#ED1C24] disabled:opacity-60"
              type="submit"
              disabled={loggingIn}
            >
              <span>Log In</span>
              {loggingIn && <Loader className="h-4 w-4" />}
            </button>
          </form>
        ) : (
          <div className="w-full max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-lg font-bold text-[#1845a3]">Admin Portal</h1>
              <button className="text-xs font-medium text-error" type="button" onClick={handleLogout}>
                Log Out
              </button>
            </div>

            <div className="flex gap-2 bg-white rounded-2xl shadow-sm p-1.5 mb-5">
              <button type="button" className={tabClass("export")} onClick={() => setActiveTab("export")}>
                Export Data
              </button>
              <button type="button" className={tabClass("purge")} onClick={() => setActiveTab("purge")}>
                Purge Data
              </button>
              <button type="button" className={tabClass("certificates")} onClick={() => setActiveTab("certificates")}>
                Certificates
              </button>
            </div>

            {activeTab === "export" && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-base font-bold text-[#1845a3]">Export Data</h2>
                <p className="mt-1 text-xs text-gray-500">
                  Download family scans, health and insurance questionnaires (tagged to referral code) for a date range.
                </p>

                <form onSubmit={handleDownload}>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor="start-date">Start date</label>
                      <input
                        id="start-date"
                        className={inputClass}
                        type="date"
                        max={today}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={downloading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor="end-date">End date</label>
                      <input
                        id="end-date"
                        className={inputClass}
                        type="date"
                        max={today}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={downloading}
                      />
                    </div>
                  </div>

                  {exportError.length > 0 && <p className="mt-3 text-xs text-error">{exportError}</p>}
                  {exportInfo.length > 0 && <p className="mt-3 text-xs text-green-600">{exportInfo}</p>}

                  <button
                    className="mt-6 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#1845a3] to-[#ED1C24] disabled:opacity-60"
                    type="submit"
                    disabled={downloading}
                  >
                    <span>{downloading ? "Preparing..." : "Download Excel"}</span>
                    {downloading && <Loader className="h-4 w-4" />}
                  </button>
                </form>
              </div>
            )}

            {activeTab === "purge" && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-base font-bold text-[#ED1C24]">Purge Data</h2>
                <p className="mt-1 text-xs text-gray-500">
                  Permanently delete all family scans, health and insurance questionnaires created within a date range.
                  A data purging certificate is generated automatically.
                </p>
                <div className="mt-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
                  <p className="text-xs text-red-700 font-medium">
                    This action is irreversible. Deleted records cannot be recovered. Consider exporting the data first.
                  </p>
                </div>

                <form onSubmit={openConfirm}>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor="purge-start">Start date</label>
                      <input
                        id="purge-start"
                        className={inputClass}
                        type="date"
                        max={today}
                        value={purgeStart}
                        onChange={(e) => setPurgeStart(e.target.value)}
                        disabled={purging}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600" htmlFor="purge-end">End date</label>
                      <input
                        id="purge-end"
                        className={inputClass}
                        type="date"
                        max={today}
                        value={purgeEnd}
                        onChange={(e) => setPurgeEnd(e.target.value)}
                        disabled={purging}
                      />
                    </div>
                  </div>

                  {purgeError.length > 0 && <p className="mt-3 text-xs text-error">{purgeError}</p>}
                  {purgeInfo.length > 0 && <p className="mt-3 text-xs text-green-600">{purgeInfo}</p>}

                  <button
                    className="mt-6 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#ED1C24] disabled:opacity-60"
                    type="submit"
                    disabled={purging}
                  >
                    <span>Delete Data &amp; Issue Certificate</span>
                  </button>
                </form>
              </div>
            )}

            {activeTab === "certificates" && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-bold text-[#1845a3]">Purging Certificates</h2>
                  <button
                    className="text-xs font-medium text-[#1845a3] disabled:opacity-50"
                    type="button"
                    onClick={fetchCertificates}
                    disabled={loadingCerts}
                  >
                    {loadingCerts ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">Log of all data purging certificates issued.</p>

                {certsError.length > 0 && <p className="mt-3 text-xs text-error">{certsError}</p>}

                {loadingCerts ? (
                  <div className="flex justify-center py-10">
                    <Loader className="h-6 w-6 text-[#1845a3]" />
                  </div>
                ) : certificates.length === 0 ? (
                  <p className="mt-6 text-sm text-gray-400 text-center py-8">No certificates issued yet.</p>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                          <th className="py-2 pr-3 font-semibold">Certificate</th>
                          <th className="py-2 pr-3 font-semibold">Period</th>
                          <th className="py-2 pr-3 font-semibold">Deleted</th>
                          <th className="py-2 pr-3 font-semibold">Issued</th>
                          <th className="py-2 font-semibold"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {certificates.map((c) => (
                          <tr key={c.id} className="border-b border-gray-50 text-xs text-gray-700">
                            <td className="py-3 pr-3 font-mono text-[11px]">{c.certificate_number}</td>
                            <td className="py-3 pr-3 whitespace-nowrap">
                              {formatDate(c.start_date)} &ndash; {formatDate(c.end_date)}
                            </td>
                            <td className="py-3 pr-3 font-semibold">{c.total_deleted}</td>
                            <td className="py-3 pr-3 whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                            <td className="py-3">
                              <button
                                className="text-[#1845a3] font-medium hover:underline"
                                type="button"
                                onClick={() => downloadCertificate(c)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {showPurgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-base font-bold text-[#ED1C24]">Confirm Permanent Deletion</h3>
            <p className="mt-2 text-xs text-gray-600">
              You are about to permanently delete all records created between{" "}
              <span className="font-semibold">{formatDate(purgeStart)}</span> and{" "}
              <span className="font-semibold">{formatDate(purgeEnd)}</span>. This cannot be undone.
            </p>
            <label className="block mt-4 text-xs font-medium text-gray-600">
              Type <span className="font-bold">DELETE</span> to confirm
            </label>
            <input
              className={inputClass}
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={purging}
              autoFocus
            />

            {purgeError.length > 0 && <p className="mt-3 text-xs text-error">{purgeError}</p>}

            <div className="mt-6 flex gap-3">
              <button
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 disabled:opacity-60"
                type="button"
                onClick={() => setShowPurgeConfirm(false)}
                disabled={purging}
              >
                Cancel
              </button>
              <button
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#ED1C24] disabled:opacity-50"
                type="button"
                onClick={handlePurge}
                disabled={purging || confirmText !== "DELETE"}
              >
                <span>{purging ? "Deleting..." : "Delete"}</span>
                {purging && <Loader className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminPanel;
